// Public product catalog — hardware SKUs from the Nventr SKU catalog (§14 of
// the business plan) plus the software SKU summary. Images are product-concept
// renders with Nventr branding; final industrial design TBD with OEM partners.

export const HARDWARE_SKUS = [
  {
    sku: 'NV-AGENT',
    name: 'Always-On Agent Appliance',
    image: '/products/nv-agent.png',
    tagline: 'A purpose-built always-on computer for agents. Not a chat box.',
    description:
      'An ultra-compact always-on appliance running agentOS: capability ' +
      'manifests (not prompt soup) connect your agents to Gmail, Calendar, ' +
      'Slack, Drive, GitHub, Notion and 200+ tools with approval gates and ' +
      'spend/identity hooks. Routine steps run on a local small model; heavy ' +
      'inference routes to NV-HOME, private Edge capacity, or your own ' +
      'frontier API keys.',
    specs: [
      'Ultra-compact (~deck-of-cards scale) · always-on',
      'agentOS preloaded · phone/app pairing · approval gates',
      'Passkey-style integrations via capability manifests',
      'Structural safety: bounded extraction · runtime gates · credentials never enter the model',
      'Model path: local small model · BYO frontier API · nventr Edge',
      'Optional runtime plan $9–29/mo — or BYO keys only',
    ],
    price: '$499 – $799',
    priceNote: 'One-time hardware · optional agentOS consumer plan after trial',
  },
  {
    sku: 'NV-SCREEN',
    name: 'Screen-Aware Computer-Use Bridge',
    image: '/products/nv-screen.png',
    tagline: 'Let agents see and act on any PC screen — no OS drivers.',
    description:
      'A slim HDMI + USB-C bridge: 4K HDMI capture in, USB-HID keyboard and ' +
      'mouse out, so an agentOS computer-use specialist can operate any ' +
      'machine with a physical confirm for high-risk actions. An optional ' +
      'on-device 8B-class model handles low-latency screen parsing; heavier ' +
      'steps route to NV-AGENT, Edge, or an API.',
    specs: [
      'HDMI 2.0 4K capture · USB-HID keyboard/mouse injection',
      'Physical confirm for high-risk actions',
      'Optional on-device 8B-class model for screen parse',
      'agentOS computer-use specialist · approval gates',
      'Works with NV-AGENT or standalone with BYO API',
      'Coding/debug assist · cross-app workflows · attended RPA',
    ],
    price: '$249 – $399',
    priceNote: 'Pairs with NV-AGENT · standalone with BYO API',
  },
  {
    sku: 'NV-HOME',
    name: 'Home Privacy Appliance — private LLM + agent sibling',
    image: '/products/nv-home.png',
    tagline: 'Private local AI for the home — and the heavy brain your agents call.',
    description:
      'A desktop-cube appliance (Mac Studio class) that runs ~200B-class ' +
      'quantized models entirely on-premises — now with the agentOS runtime ' +
      'enabled, so it is both a private LLM box and the high-capacity local ' +
      'brain NV-AGENT calls for inference that never leaves the premises. ' +
      'Private mode is the default, with a hard off-switch for any shared ' +
      'contribution. nventr Edge OS comes preloaded with a 3–6 month trial.',
    specs: [
      'Spark / GB10-class SoC · ~128 GB unified memory',
      'Runs ~200B-class quantized models locally',
      'agentOS runtime enabled — run agents that never leave home',
      'Idle ~35–45 W · peak ~240 W',
      '1/2.5/10 GbE · zero-inbound agent · dual-VLAN ready',
      'Measured boot · TPM · private-mode default · hard off-switch',
      'Edge OS private mode preloaded · 3–6 mo trial → $15–40/yr',
    ],
    price: '$3,000 – $4,800',
    priceNote: 'ISP subsidies common · OS trial included',
    earningsNote: 'Opt-in contribution: $50–200/yr — privacy first, income optional',
  },
  {
    sku: 'NV-HYDRO',
    name: 'Compute Water Heater — 50-gal AI-heated tank',
    image: '/products/nv-hydro.jpg',
    tagline: 'The water heater that thinks: every compute watt becomes hot water.',
    description:
      'A standard-size residential hot water tank heated by an immersed, ' +
      'liquid-cooled nventr compute sled instead of a bare resistive ' +
      'element. The sled runs the same local models and agentOS workloads ' +
      'as NV-HOME — and because the tank captures essentially all of its ' +
      'dissipated watts, the electricity your compute burns does double ' +
      'duty as the hot water you were paying to make anyway. Private mode ' +
      'is the default; opt in to pool contribution and pool workloads keep ' +
      'the tank hot with power reimbursed. A conventional backup element ' +
      'covers heavy draws and recovery.',
    specs: [
      '50-gal (189 L) stainless tank · standard footprint (Ø ~24 in × 60 in)',
      'Immersed liquid-cooled sled: GB10-class SoC · ~0.6–1.2 kW sustained',
      '~95%+ of compute watts captured as water heat · 4.5 kW backup element',
      'Covers most of a typical household hot-water load (~12 kWh/day)',
      '240 V / 30 A · 2.5 GbE · dual-VLAN ready · zero-inbound agent',
      'Private mode default · hard off-switch · opt-in contribution like NV-HOME',
      'Edge OS home runtime preloaded · 3–6 mo trial → $15–40/yr',
      'Certified plumbing partner install · drop-in electric-tank replacement',
    ],
    price: '$3,400 – $5,200',
    priceNote: 'One-time · replaces a standard electric water heater · partner install',
    earningsNote:
      'Opt-in contribution: ~$450–650/yr water-heating offset (power reimbursed) + $50–200/yr',
  },
  {
    sku: 'NV-BIZ-MID',
    name: 'Business Node — 2-GPU class',
    image: '/products/nv-biz-mid.png',
    tagline: 'Host it, don\u2019t buy it: your premises, your ISP\u2019s metal, your earnings.',
    description:
      'A 2U rack-mount (or short-depth tower) business node built around dual ' +
      'workstation/datacenter GPUs. Your ISP deploys and owns the node on ' +
      'your premises — you lend a rack or closet with zero capital outlay and ' +
      'earn an availability floor plus revenue share, stacked on free ' +
      'symmetric fiber and a hardware-partitioned private inference slice ' +
      'for your own workloads.',
    specs: [
      '2× RTX PRO 6000-class or 2× H200 NVL-class GPUs',
      'Server CPU · 512 GB–1 TB RAM · NVMe · dual PSU option',
      '~1.5–3 kW sustained under load',
      'Dual 10/25/100 GbE · static IP · dual-VLAN ONT path',
      'SEV-SNP/TDX · GPU CC path · enclave TLS · T2 default',
      'Edge OS commercial + private partition · $3k/GPU/yr (T2) after trial',
    ],
    price: '$0 to host',
    priceNote: 'ISP-owned metal (~$65–210k BOM carried by the operator, not you)',
    earningsNote:
      'Host earnings potential ~$14–17k/yr at baseline — up to ~$23k/yr at high utilization',
  },
  {
    sku: 'NV-BIZ-DENSE',
    name: 'Business Node — dense multi-GPU',
    image: '/products/nv-biz-dense.png',
    tagline: 'Strategic edge capacity for ISPs and sovereigns — the fleet\u2019s strongest economics.',
    description:
      'A 4U high-density chassis (8× MI300X-class illustrative build) for ' +
      'telcos and sovereign buyers deploying serious throughput with the ' +
      'full attestation chain: fiber retention, enterprise SLAs, and ' +
      'jurisdiction-pinned inference in one box. The strongest economics in ' +
      'the fleet — and the best candidate for thermal recovery, with up to ' +
      '~$2.6k/yr of usable heat offset for the host.',
    specs: [
      '8× MI300X-class or equivalent dense build',
      'Dual CPU · 1–2 TB RAM · high-speed NVMe · redundant PSU',
      '~5–8+ kW sustained · dedicated circuit / submeter required',
      '100 GbE+ · failure-domain-aware placement',
      'T2/T3 security · full attestation chain · dual-VLAN non-negotiable',
      'Edge OS $3k/GPU/yr (T2 · ~$24k/node) · preferred ISP-owned',
    ],
    price: 'ISP / sovereign program',
    priceNote: 'From ~$212k BOM · 12–20% markup · operator-funded — hosts pay nothing',
    earningsNote:
      'Host earnings potential ~$28k/yr at baseline — up to ~$51k/yr at high ' +
      'utilization + premium lane',
  },
  {
    sku: 'NV-THX-HOME',
    name: 'Thermal Recovery Kit — light',
    image: '/products/nv-thx-home.png',
    tagline: 'Turn your appliance’s waste heat into hot water.',
    description:
      'A compact closed-loop liquid heat exchanger that captures waste heat ' +
      'from the home appliance and delivers it to domestic hot-water pre-heat ' +
      'or a small hydronic assist. Installed by certified HVAC partners; best ' +
      'value in cold climates.',
    specs: [
      '~150–220 W useful heat · ~500–750 BTU/hr continuous',
      'Compact HX module · quick-connects · braided lines',
      'Isolation valves + sensors included',
      'Illustrative offset ~$85/yr @ $0.14/kWh-eq',
      'Certified HVAC partner install',
    ],
    price: '$450 – $650',
    priceNote: 'Add-on SKU for NV-HOME',
  },
  {
    sku: 'NV-THX-BIZ',
    name: 'Thermal Recovery Kit — business',
    image: '/products/nv-thx-biz.png',
    tagline: 'Recover node heat into HVAC, spa, or pool loops.',
    description:
      'Liquid-to-water recovery for business nodes: standard kits feed HVAC ' +
      'hydronic or spa loops, dense kits deliver a meaningful heating assist. ' +
      'Optional thermal metering surfaces recovered kWh in the Edge OS host ' +
      'portal. Code compliance and liability stay on the kit + installer path.',
    specs: [
      'Standard ~1–2.5 kW · dense ~4–7 kW useful heat',
      'HX tank/module · pump · sensors · temp display · isolation',
      'Offsets: mid ~$850/yr · dense ~$2,600/yr illustrative',
      'Optional thermal kWh metering in the host portal',
      'HVAC / pool / ISP field-partner install',
    ],
    price: '$1,900 – $5,500',
    priceNote: 'Standard $1,900–2,800 · dense $3,800–5,500',
  },
];

// ---------------------------------------------------------------------------
// Shared-pool earnings model (business plan §4.1–4.2, §6.1, §12.2).
//
// Plan baseline: 50% utilization, $0.25/M commodity token lane, 12% host
// revenue share, $1,200/yr availability floor, free-fiber value $6–18k/yr
// (midpoint $12k used for the derived share anchors). Revenue-share dollars
// scale ~linearly with utilization and token price, so the tiers below scale
// the plan's 50%-utilization anchors by utilization (×0.6 / ×1.0 / ×1.6) and
// lane ($0.40 sovereignty = ×1.6 vs $0.25). All figures illustrative,
// directional, pre-pilot.

const share = (base) => ({
  light: [Math.round(base * 0.6), Math.round(base * 0.6 * 1.6)],
  baseline: [base, Math.round(base * 1.6)],
  high: [Math.round(base * 1.6), Math.round(base * 1.6 * 1.6)],
});

export const EARNINGS = {
  intro:
    'Don\u2019t buy the metal — host it. Lend a rack or closet, keep zero ' +
    'capital at risk, and your ISP\u2019s node earns for you: a guaranteed ' +
    'availability floor plus a revenue share (8–15% of node gross, 12% ' +
    'modeled), stacked on free symmetric fiber + static IP from the telco ' +
    'partner — and an optional thermal kit turns waste heat into a utility ' +
    'offset. NV-HOME owners can opt in on the same terms, privacy first.',
  utilTiers: [
    { id: 'light', label: 'Light · 30% util' },
    { id: 'baseline', label: 'Baseline · 50% util' },
    { id: 'high', label: 'High · 80% util' },
  ],
  lanes: ['Commodity lane $0.25/M', 'Sovereignty lane $0.40/M'],
  fixed: [
    { label: 'Availability floor (guaranteed)', value: '$1,200 / yr' },
    { label: 'Free symmetric fiber + static IP (value)', value: '$6,000 – $18,000 / yr' },
    { label: 'Host power', value: 'Reimbursed — net zero' },
    { label: 'Private inference slice', value: 'Included (hardware-partitioned)' },
  ],
  hostConfigs: [
    {
      sku: 'NV-BIZ-MID',
      config: '2× RTX PRO 6000-class',
      share: share(800),
      thermal: 850,
      totalBaseline: '~$14,000 / yr',
    },
    {
      sku: 'NV-BIZ-MID',
      config: '2× H200 NVL-class',
      share: share(3800),
      thermal: 850,
      totalBaseline: '~$17,000 / yr',
    },
    {
      sku: 'NV-BIZ-DENSE',
      config: '8× MI300X-class',
      share: share(14800),
      thermal: 2600,
      totalBaseline: '~$28,000 / yr',
    },
  ],
  home: {
    sku: 'NV-HOME',
    note:
      'NV-HOME is a privacy appliance first — income is optional and ' +
      'best-effort, never required. With contribution mode opted in (hard ' +
      'off-switch, private data never enters the pool):',
    lines: [
      { label: 'Opt-in idle contribution', value: '$50 – $200 / yr' },
      { label: 'Thermal offset (NV-THX-HOME kit, cold climates)', value: '~$85 / yr utility savings' },
      { label: 'NV-HYDRO water-heating offset (contribution mode, power reimbursed)', value: '~$450 – $650 / yr' },
    ],
  },
  operator: {
    note:
      'Nobody should buy these nodes for token yield — the numbers below ' +
      'make that plain, and they are published on purpose. Owning the metal ' +
      'is for ISPs and operators underwriting strategic value: a hosted node ' +
      'retains a business fiber account (worth $6–18k/yr on its own), ' +
      'creates enterprise SLA and jurisdiction-pinned products hyperscalers ' +
      'don\u2019t offer, and the token margin merely offsets opex so the ' +
      'strategic capacity runs near self-funding. For transparency, the ' +
      'token economics of ownership (before Edge OS license; 50% ' +
      'utilization):',
    rows: [
      {
        config: '2× RTX PRO 6000 · $38k capex',
        commodity: '–$13,600 / yr',
        sovereignty: 'Modest positive',
        payback: 'Never → long',
      },
      {
        config: '2× H200 NVL · $72k capex',
        commodity: '–$5,600 / yr',
        sovereignty: '+$8,000 – 12,000 / yr',
        payback: 'Never → ~6 yr',
      },
      {
        config: '8× MI300X · $212k capex',
        commodity: '+$21,600 / yr',
        sovereignty: '+$45,000 – 55,000 / yr',
        payback: '~10 yr → 4–5 yr',
      },
    ],
    readthrough:
      'Only dense high-bandwidth builds clear positive operator margin at ' +
      'commodity token prices — mid-tier configs need the sovereignty ' +
      'premium or higher utilization. Revenue is bandwidth-bound, not ' +
      'capacity-bound.',
  },
  // OS-fee impact on owner-operators (hosts are unaffected — the ISP pays
  // the license). Operator gross margins from §4.1 (50% util, before OS).
  // Fees use the market-anchored per-GPU price book (repriced 2026-08-10
  // vs NVIDIA AI Enterprise / OpenShift): T1 $2k/GPU, T2 $3k/GPU,
  // T3 $3.5k/GPU + $5k/node. T1 serves the commodity lane; T2/T3 unlock
  // the sovereignty lane.
  osImpact: {
    intro:
      'Buying and operating the node yourself? The Edge OS license is the ' +
      'biggest line between gross token margin and your net. Fee take and ' +
      'ROI by hardware tier and use case, at the per-GPU price book ' +
      '(50% utilization) — hosts skip this entirely: on the preferred path ' +
      'the ISP/operator pays the OS license, and host floor + share are net.',
    scenarios: [
      'Open · T1 $2k/GPU · $0.25/M',
      'Confidential · T2 $3k/GPU · $0.40/M',
      'Sovereign · T3 $3.5k/GPU + $5k · $0.40/M',
    ],
    rows: [
      {
        build: '2× RTX PRO 6000 · $38k capex',
        breakeven: '~$2k (sovereignty) · none (commodity)',
        cells: [
          { net: '–$17,600', take: 'token margin negative pre-fee', roi: 'Not viable' },
          { net: '–$4,000', take: 'fee > margin', roi: 'Not viable' },
          { net: '–$10,000', take: 'fee > margin', roi: 'Not viable' },
        ],
      },
      {
        build: '2× H200 NVL · $72k capex',
        breakeven: '~$10k (sovereignty) · none (commodity)',
        cells: [
          { net: '–$9,600', take: 'token margin negative pre-fee', roi: 'Not viable' },
          { net: '+$4,000', take: '60% of gross', roi: '5.6%/yr · ~18 yr payback' },
          { net: '–$2,000', take: 'fee > margin', roi: 'Not viable' },
        ],
      },
      {
        build: '8× MI300X dense · $212k capex',
        breakeven: '~$21.6k (commodity) · ~$50k (sovereignty)',
        cells: [
          { net: '+$5,600', take: '74% of gross', roi: '2.6%/yr · ~38 yr payback' },
          { net: '+$26,000', take: '48% of gross', roi: '12.3%/yr · ~8 yr payback' },
          { net: '+$17,000', take: '66% of gross', roi: '8.0%/yr · ~12.5 yr payback' },
        ],
      },
    ],
    home: {
      label: 'NV-HOME (opt-in contribution)',
      detail:
        'Edge OS runtime $15–40/yr after trial vs $50–200/yr opt-in ' +
        'contribution → fee takes 8–80% of contribution, net $10–185/yr ' +
        '(+~$85 thermal). A privacy appliance, not an income product — the ' +
        'OS fee is buying private local AI, not pool access.',
    },
    readthrough:
      'Per-GPU pricing scales the fee with the hardware class ' +
      'automatically: a dense node pays ~$24k at T2 — close ' +
      'to the old flat band — while a 2-GPU node pays ~$6k instead of ' +
      '$20–30k, turning mid-tier owner-operators from impossible to ' +
      'merely thin. What pricing cannot fix: mid-tier token economics are ' +
      'weak at commodity prices regardless of fee (D2\u2019s sovereignty ' +
      'premium is the real lever), and T3\u2019s adder only pays for itself ' +
      'when jurisdiction-pinned deals carry premium $/M or higher ' +
      'placement weight. A caution on the ROI column: at the plan\u2019s ' +
      'stated 4-year hardware life, even the viable builds do not recover ' +
      'capex at 50% utilization (8–18 yr simple payback vs a 4-yr ' +
      'depreciation clock). Owner-operating pencils only at high ' +
      'utilization (dense sovereignty at 80% util pays back in ~4 yr), a ' +
      'longer effective life (5–7 yr inference duty is plausible), or ' +
      'meaningful resale residual — which is exactly why the plan prefers ' +
      'the host path, where the ISP carries the hardware-life risk.',
  },
  assumptions:
    'Illustrative and directional (pre-pilot), derived from the plan\u2019s ' +
    'stated assumptions: 12% host share of gross (8–15% band), $1,200/yr ' +
    'availability floor, $0.12/kWh power (reimbursed), 4-year hardware ' +
    'life, fiber value modeled at the $12k midpoint of $6–18k/yr. Revenue ' +
    'share scaled linearly with utilization and token price from the 50% / ' +
    '$0.25-per-M anchors. Actual earnings depend on placement, demand, SLA ' +
    'performance, and contract terms.',
};

// Repriced 2026-08-10 against market comps: Edge OS per-GPU (anchor: NVIDIA
// AI Enterprise $4,500/GPU/yr, OpenShift ~$8.7k/socket-pair/yr); agentOS
// bands vs agent-platform market (Agentforce $2/conv · $0.10/action, Copilot
// Studio $200/mo per 25k credits, UiPath real ACVs ~$26k SMB / ~$430k ent).
export const SOFTWARE_SKUS = [
  {
    sku: 'Platform — Start',
    buyer: 'Hosted workflow runs on an existing stack',
    price: '$15 / mo',
    detail: '1 builder · 3,000 workflow runs · shared nQube · no dedicated URL, DB, or repo',
  },
  {
    sku: 'Platform — Team',
    buyer: 'Ops / finance on an existing stack',
    price: '$39 / mo',
    detail: 'Shared nQube + production connectors · 3 builders + 10 operators · 12,000 workflow runs · no instance',
  },
  {
    sku: 'Platform — Business',
    buyer: 'Multi-workflow · SAML',
    price: '$599 / mo',
    detail: '10 builders + 25 operators · 40,000 workflow runs · SAML · still no instance',
  },
  {
    sku: 'Platform — Enterprise',
    buyer: 'Multi-BU · optional VPC nodes',
    price: 'from $8,000 / mo',
    detail: '25 builders + 100 operators · 400,000 Runtime Credits · SCIM · named support · typical $8k',
  },
  {
    sku: 'Build',
    buyer: 'Need a public site first',
    price: '$15 / mo',
    detail: 'Shared host · 120 build credits · export the layer · no dedicated database or Dev Console',
  },
  {
    sku: 'App Kit',
    buyer: 'Need an app people log into',
    price: '$149 / mo',
    detail: 'License for the load path, health checks, and updates · Dev Console included · public site is the front door · managed host included',
  },
  {
    sku: 'App Kit — Business',
    buyer: 'Production instance',
    price: '$349 / mo',
    detail: 'Unlimited viewers on this instance · 40 Dev Console runs · 800 compute hours · SAML · still not org-wide Platform',
  },
  {
    sku: 'App Kit — Enterprise',
    buyer: 'Multi-instance / license / air-gap',
    price: 'from $3,000 / mo',
    detail: 'Instance rent + Dev Console · BYOM zeros the model line, not the rent',
  },
  {
    sku: 'Bundle — Kit + Platform Team',
    buyer: 'Shipping an app that also commits into existing systems',
    price: '$160 / mo',
    detail: 'Written attach: Kit $149 + Platform Team $39 · 15% off $188',
  },
  {
    sku: 'Bundle — Kit Business + Platform Business',
    buyer: 'Department cart — app plus org-wide pipelines',
    price: '$825 / mo',
    detail: 'Written attach: Kit Business $349 + Platform Business $599 · 13% off $948',
  },
  {
    sku: 'Pipeline pilot (delivery, not a plan)',
    buyer: 'Platform — first gated pipeline on a real system',
    price: '$9,000 – $24,000',
    detail: 'Delivery of Platform, not a first custom app · typical package from $15k',
  },
  {
    sku: 'Edge OS — T1 Open',
    buyer: 'Telcos / ISPs — batch & non-sensitive lanes',
    price: '$2,000 / GPU / yr',
    detail: 'Measured boot + signed runtime (2-GPU node ~$4k · dense ~$16k)',
  },
  {
    sku: 'Edge OS — T2 Confidential',
    buyer: 'Telcos / ISPs — default business nodes',
    price: '$3,000 / GPU / yr',
    detail: 'SEV-SNP + GPU CC + enclave TLS + dual-VLAN (2-GPU ~$6k · dense ~$24k) — attestation the hyperscaler stack doesn\u2019t ship',
  },
  {
    sku: 'Edge OS — T3 Sovereign',
    buyer: 'Sovereign / regulated deals',
    price: '$3,500 / GPU / yr + $5k / node',
    detail: 'T2 + jurisdiction pin + audit adder + higher SLA weight',
  },
  {
    sku: 'Edge OS — Home runtime',
    buyer: 'Homeowners (post-trial)',
    price: '$15 – $40 / yr',
    detail: 'Private mode · trial included with hardware',
  },
];
